import React, { useMemo, useState } from 'react';
import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd';
import { Lock, Unlock, GripVertical, AlertTriangle, ChevronUp, ChevronDown, BatteryLow } from 'lucide-react';
import { StrictModeDroppable } from '../../../src/components/StrictModeDroppable';
import { PriorityRow, RateioModalidade, RateioRowState, UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';
import { FaturaConsumoResumo, LIMIAR_ALERTA_DELTA_PERCENT, MARGEM_ERRO_PADRAO_PERCENT, calcularDelta, calcularProximaLeitura } from './consumoHistorico';
import { consumoOperacionalDaUc } from '../../../src/lib/consumoDaUc';
import {
  COBERTURA_MAXIMA_SAUDAVEL_MESES,
  COBERTURA_MINIMA_SAUDAVEL_MESES,
  type CoberturaSaldo,
} from './coberturaSaldo';
import type { ProjecaoUc } from './projecaoUc';
import DeltaPopover from './DeltaPopover';
import UcAcoesMenu from './UcAcoesMenu';
import InfoTip from '../../../src/components/InfoTip';

interface RateioTabelaUCsProps {
  modalidade: RateioModalidade;
  rows: RateioRowState[];
  ordemPrioridade: PriorityRow[];
  ucsById: Map<number, UcParaRateio>;
  historicoPorUc: Map<number, FaturaConsumoResumo[]>;
  /**
   * Projeção do próximo mês por UC — o insumo da decisão "quanto injetar nesta
   * UC". Derivada do histórico já carregado, nunca persistida.
   */
  projecaoPorUc?: Map<number, ProjecaoUc>;
  /** % utilizado no rateio anterior dessa UC nessa UG (referência histórica, só leitura). */
  percentualAnteriorPorUc?: Map<number, number>;
  onEditarPercentual: (ucId: number, valor: number) => void;
  onToggleLock: (ucId: number) => void;
  onDragEnd: (sourceIndex: number, destIndex: number) => void;
  onAplicarSugestaoDelta: (ucId: number, consumoRealKwh: number) => void;
  onUcAlterada: () => void;
  /** UCs com CPF/CNPJ da fatura divergente do cadastro (Seção 12 — alerta de titularidade). */
  titularidadeDivergente?: Set<number>;
  /** UCs com crédito possivelmente reivindicado em mais de um rateio (Seção 12 — alerta de duplicidade). */
  creditosDuplicados?: Set<number>;
  /** Meses de consumo que o saldo de créditos cobre, por UC — ver `coberturaSaldo.ts`. */
  coberturaSaldoPorUc?: Map<number, CoberturaSaldo>;
  /** Dia de leitura padrão da distribuidora da UG atual — usado na previsão de leitura (Seção 10). */
  diaLeituraPadrao?: number | null;
  readOnly?: boolean;
}

type DeltaAberto = { ucId: number; anchor: DOMRect };

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const nomeTitular = (uc?: UcParaRateio) => uc?.nome_cliente || '—';
const numeroUc = (uc?: UcParaRateio) => uc?.numero_uc || '—';
const consumoMedio = (uc?: UcParaRateio) => (uc?.consumo_medio_kwh != null ? formatKwh(uc.consumo_medio_kwh) : '—');

/**
 * Consumo que o rateio está usando + o que se espera do PRÓXIMO mês.
 *
 * Fica na mesma célula de propósito: é aqui que o analista olha antes de digitar
 * o percentual da linha, e a decisão dele é sobre o mês que vem, não sobre o que
 * já passou. Sem isso a projeção existia só como total no rodapé — visível
 * depois que a alocação já estava feita, tarde demais para guiá-la.
 */
const ConsumoComProjecao: React.FC<{ uc?: UcParaRateio; projecao?: ProjecaoUc }> = ({ uc, projecao }) => {
  const consumo = consumoOperacionalDaUc(uc);
  const base = consumo.valorKwh != null ? formatKwh(consumo.valorKwh) : '—';
  const mes = projecao?.projecao?.[0];
  const semBase = !projecao || projecao.confianca === 'sem_dados' || projecao.mesesObservados < 3;
  // Estimativa cadastral usada porque o observado ficou desatualizado (job
  // diário parado) é um sinal de falha operacional, não uma nota de rodapé —
  // por isso ganha tom de alerta em vez do cinza padrão do InfoTip.
  const estimativaPorFalha = consumo.fonte === 'estimativa_cadastral' && !!consumo.aviso?.includes('não é atualizado');

  return (
    <span className="block leading-tight">
      <span>
        {base}
        {consumo.fonte === 'estimativa_cadastral' && (
          <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">est.</span>
        )}
        {/* O número instável continua à vista — a marca avisa, não esconde. */}
        {consumo.suspeito && (
          <span className="ml-1 text-[10px] uppercase tracking-wide text-red-600 dark:text-red-400">instável</span>
        )}
        {consumo.aviso && (
          <InfoTip
            label={consumo.suspeito ? 'Por que este consumo está marcado como instável' : 'Por que este valor é uma estimativa'}
            tone={estimativaPorFalha || consumo.suspeito ? 'danger' : 'default'}
          >
            {consumo.aviso}
          </InfoTip>
        )}
      </span>
      {semBase ? (
        <span className="block text-[11px] text-gray-400 dark:text-gray-500">
          sem projeção
          <InfoTip label="Por que esta UC não tem projeção">
            {projecao?.avisos?.[0] ||
              'Sem faturas suficientes (mínimo de 3) para projetar o próximo mês desta UC.'}
          </InfoTip>
        </span>
      ) : (
        <span className="block text-[11px] text-primary">
          ~{formatKwh(mes!.consumoProjetadoKwh)} no próx. mês
          <InfoTip label="Como esta projeção foi calculada">
            {mes!.rotulo}:{' '}
            {mes!.metodo === 'sazonal'
              ? `baseado em ${mes!.baseRotulo} (${formatKwh(mes!.baseConsumoKwh || 0)} kWh)`
              : 'média observada, sem o mesmo mês do ano anterior'}
            {projecao!.fatorTendenciaLimitado ? ' · tendência limitada' : ''}
          </InfoTip>
        </span>
      )}
    </span>
  );
};

const rotuloMeses = (n: number) =>
  `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${
    Math.abs(n) > 1 ? 'meses' : 'mês'
  }`;

/**
 * Saldo do mês (a coluna de sempre) + quantos meses de consumo o ESTOQUE de
 * créditos ainda cobre.
 *
 * São dois números diferentes da mesma fatura, e a distinção não é decorativa:
 * o de cima é o resultado do ciclo, o de baixo é o lastro que a UC leva para o
 * próximo — é ele que responde "esta UC entra descoberta?". Por isso a conta
 * aparece aqui embaixo do saldo, e não como mais um número solto no topo. Abaixo
 * de 1 mês ganha âmbar; nada é ajustado sozinho, a decisão é do analista.
 */
const SaldoComCobertura: React.FC<{ saldoMesKwh?: number | null; cobertura?: CoberturaSaldo }> = ({
  saldoMesKwh,
  cobertura,
}) => (
  <span className="block leading-tight">
    <span>{saldoMesKwh != null ? formatKwh(saldoMesKwh) : '—'}</span>
    {cobertura ? (
      <span
        className={`flex items-center gap-1 text-[11px] ${
          cobertura.abaixoDoMinimo ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {cobertura.abaixoDoMinimo ? <BatteryLow size={12} className="shrink-0" /> : null}
        {rotuloMeses(cobertura.coberturaMeses)} de cobertura
        <InfoTip
          label="Como esta cobertura foi calculada"
          tone={cobertura.abaixoDoMinimo ? 'warning' : 'default'}
        >
          Saldo de créditos{cobertura.mesReferencia ? ` em ${cobertura.mesReferencia}` : ''}:{' '}
          {formatKwh(cobertura.saldoCreditosKwh)} kWh ÷ consumo de {formatKwh(cobertura.consumoBaseKwh)} kWh ={' '}
          {rotuloMeses(cobertura.coberturaMeses)}. A faixa saudável vai de {COBERTURA_MINIMA_SAUDAVEL_MESES} a{' '}
          {COBERTURA_MAXIMA_SAUDAVEL_MESES} meses.
          {cobertura.abaixoDoMinimo
            ? ' Abaixo disso a UC pode entrar no próximo ciclo sem lastro.'
            : ''}{' '}
          O saldo usado é o SALDO KWH da fatura (o estoque de créditos), diferente do saldo do mês mostrado acima.
          {cobertura.fonteConsumo === 'estimativa_cadastral'
            ? ' O consumo usado é a estimativa da validação de dados, não a média das faturas.'
            : ''}
          {cobertura.consumoSuspeito
            ? ' Atenção: o consumo desta UC está marcado como instável — confira o histórico antes de decidir.'
            : ''}
        </InfoTip>
      </span>
    ) : null}
  </span>
);

/** Sinal compacto para o card mobile, que é só leitura — a conta inteira fica na grade. */
const ChipSaldoBaixo: React.FC<{ cobertura?: CoberturaSaldo }> = ({ cobertura }) =>
  cobertura?.abaixoDoMinimo ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
      <BatteryLow size={11} /> {rotuloMeses(cobertura.coberturaMeses)} de cobertura
    </span>
  ) : null;

// Mapeamento de status da UC (Seção 12 da spec). Ativa/1º-2º Ciclo/Cancelada já
// acontecem com dado real (uc.situacao); Reativação/Pausada ficam definidos aqui
// mas hoje nunca são acionados — dependem do travamento automático (Seção 9,
// ainda não implementado).
const STATUS_TAG: Record<string, { label: string; className: string; dot: string }> = {
  Ativa: { label: 'Ativa', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', dot: '🟢' },
  'Ativa-S/-Rateio': { label: 'Ativa', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', dot: '🟢' },
  '1° Ciclo': { label: '1º Ciclo', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400', dot: '🟡' },
  '2° Ciclo': { label: '2º Ciclo', className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400', dot: '🟡' },
  Reativacao: { label: 'Reativação', className: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400', dot: '🟠' },
  Pausada: { label: 'Pausada', className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400', dot: '🔵' },
  Cancelada: { label: 'Cancelada', className: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400', dot: '🔴' },
};

const StatusTag: React.FC<{ situacao?: string | null }> = ({ situacao }) => {
  const info = (situacao && STATUS_TAG[situacao]) || {
    label: situacao || '—',
    className: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    dot: '',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${info.className}`}>
      {info.dot} {info.label}
    </span>
  );
};

const formatData = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

// Cronograma de previsão de recebimento (Seção 10) — Inclusão e Leitura usam
// dado real (data de vínculo + dia de leitura da distribuidora, Fatia 8);
// Recebimento fica sem data — o prazo entre leitura e repasse do crédito não
// está definido em lugar nenhum da spec, não dá pra inventar um número aqui.
const PrevisaoRecebimento: React.FC<{ vinculadoEm?: string | null; diaLeituraPadrao?: number | null }> = ({
  vinculadoEm,
  diaLeituraPadrao,
}) => {
  const proximaLeitura = calcularProximaLeitura(vinculadoEm, diaLeituraPadrao);
  const marcos = [
    {
      label: 'Inclusão',
      preenchido: !!vinculadoEm,
      tooltip: vinculadoEm ? `Inclusão: ${formatData(new Date(vinculadoEm))}` : 'Data de vínculo não registrada',
    },
    {
      label: 'Leitura',
      preenchido: !!proximaLeitura,
      tooltip: proximaLeitura
        ? `Próxima leitura prevista: ${formatData(proximaLeitura)}`
        : 'Depende da data de vínculo e do dia de leitura da distribuidora',
    },
    { label: 'Recebimento', preenchido: false, tooltip: 'Recebimento — prazo ainda não definido pela distribuidora' },
  ];
  return (
    <div className="flex items-center gap-1" title="Cronograma de previsão de recebimento (Inclusão → Leitura → Recebimento)">
      {marcos.map((m, i) => (
        <React.Fragment key={m.label}>
          <span title={m.tooltip} className={`w-2 h-2 rounded-full ${m.preenchido ? 'bg-emerald-400' : 'bg-gray-300 dark:bg-gray-600'}`} />
          {i < 2 && <span className="w-4 h-px bg-gray-300 dark:bg-gray-600" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// Badge da modalidade GD (Seção 14) — só cadastro/exibição, não afeta cálculo de faturamento.
const GdBadge: React.FC<{ modalidade?: 'GD_I' | 'GD_II' | null }> = ({ modalidade }) => {
  if (!modalidade) return null;
  return (
    <span
      className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
      title="Modalidade GD — só cadastro, não afeta cálculo de faturamento."
    >
      {modalidade === 'GD_I' ? 'GD I' : 'GD II'}
    </span>
  );
};

const DeltaIcon: React.FC<{ alerta: boolean; onOpen: (anchor: DOMRect) => void }> = ({ alerta, onOpen }) => (
  <button
    type="button"
    onClick={e => onOpen(e.currentTarget.getBoundingClientRect())}
    title={alerta ? `Variação de consumo acima de ${LIMIAR_ALERTA_DELTA_PERCENT}% — Revisão Inteligente (Delta)` : 'Revisão Inteligente (Delta)'}
    className={`p-1 rounded ${
      alerta
        ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
        : 'text-gray-300 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
    }`}
  >
    <AlertTriangle size={14} className={alerta ? 'animate-pulse' : ''} />
  </button>
);

/**
 * O vocabulário dos três números, explicado uma vez no cabeçalho.
 *
 * "Consumo em uso" não é sinônimo de nenhum dos dois campos: é o que o rateio
 * está aplicando AGORA, que muda de fonte conforme a UC tem ou não histórico
 * confiável. Chamar a coluna de "Consumo Observado" mentiria nas linhas que caem
 * na estimativa; chamar de "Consumo Médio" mentiria nas demais.
 */
const ConsumoEmUsoInfo: React.FC = () => (
  <InfoTip label="O que é o consumo em uso">
    O número que este rateio está aplicando para a UC.
    {' '}Vem do <strong>consumo observado</strong> (média das faturas reais dos últimos 12 meses,
    recalculada diariamente) sempre que houver pelo menos 3 faturas recentes.
    {' '}Sem essa base, cai no <strong>consumo médio do cadastro</strong> — a estimativa da análise
    cadastral, marcada com <strong>est.</strong> na linha.
  </InfoTip>
);

const HeaderRow: React.FC<{ modalidade: RateioModalidade }> = ({ modalidade }) => (
  <thead>
    <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
      <th className="py-2 pr-3">UC</th>
      <th className="py-2 pr-3">Titular</th>
      <th className="py-2 pr-3 normal-case">
        <span className="uppercase">Consumo em uso</span>
        <ConsumoEmUsoInfo />
      </th>
      <th className="py-2 pr-3">Saldo Acumulado</th>
      <th className="py-2 pr-3">Margem de Erro (%)</th>
      <th className="py-2 pr-3">Percentual (%)</th>
      <th className="py-2 pr-3">Últ. % Vigente</th>
      <th className="py-2 pr-3">Energia-Alvo (kWh)</th>
      <th className="py-2 pr-3">Status</th>
      <th className="py-2 pr-3">Previsão</th>
      <th className="py-2 pr-3 text-right">Ações</th>
    </tr>
  </thead>
);

type CampoOrdenavel = 'uc' | 'titular' | 'consumo' | 'saldo' | 'percent' | 'status';
type Ordenacao = { campo: CampoOrdenavel; direcao: 'asc' | 'desc' } | null;

// Cabeçalho ordenável (Seção 5/Tela 2 — "colunas sortable") — só faz sentido na
// modalidade Percentual Fixo; em Ordem de Prioridade a ordem das linhas *é* a
// prioridade de rateio, então ordenar quebraria o propósito da tela.
const SortableHeaderRow: React.FC<{ ordenacao: Ordenacao; onSort: (campo: CampoOrdenavel) => void }> = ({ ordenacao, onSort }) => {
  const th = (campo: CampoOrdenavel, label: string, extra?: React.ReactNode) => (
    <th className="py-2 pr-3">
      <span className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onSort(campo)}
          className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
        >
          {label}
          {ordenacao?.campo === campo && (ordenacao.direcao === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </button>
        {extra}
      </span>
    </th>
  );
  return (
    <thead>
      <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
        {th('uc', 'UC')}
        {th('titular', 'Titular')}
        {th('consumo', 'Consumo em uso', <ConsumoEmUsoInfo />)}
        {th('saldo', 'Saldo Acumulado')}
        <th className="py-2 pr-3">Margem de Erro (%)</th>
        {th('percent', 'Percentual (%)')}
        <th className="py-2 pr-3">Últ. % Vigente</th>
        <th className="py-2 pr-3">Energia-Alvo (kWh)</th>
        {th('status', 'Status')}
        <th className="py-2 pr-3">Previsão</th>
        <th className="py-2 pr-3 text-right">Ações</th>
      </tr>
    </thead>
  );
};

const RateioTabelaUCs: React.FC<RateioTabelaUCsProps> = ({
  modalidade,
  rows,
  ordemPrioridade,
  ucsById,
  historicoPorUc,
  projecaoPorUc,
  percentualAnteriorPorUc,
  onEditarPercentual,
  onToggleLock,
  onDragEnd,
  onAplicarSugestaoDelta,
  onUcAlterada,
  titularidadeDivergente,
  creditosDuplicados,
  coberturaSaldoPorUc,
  diaLeituraPadrao,
  readOnly = false,
}) => {
  const rowsByUcId = new Map(rows.map(r => [r.ucId, r]));
  const [deltaAberto, setDeltaAberto] = useState<DeltaAberto | null>(null);
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);

  const linhaDestaque = (ucId: number) => {
    if (creditosDuplicados?.has(ucId)) return 'bg-red-50/50 dark:bg-red-900/10';
    if (titularidadeDivergente?.has(ucId)) return 'bg-orange-50/50 dark:bg-orange-900/10';
    return '';
  };

  const onSort = (campo: CampoOrdenavel) => {
    setOrdenacao(prev => {
      if (prev?.campo !== campo) return { campo, direcao: 'asc' };
      if (prev.direcao === 'asc') return { campo, direcao: 'desc' };
      return null;
    });
  };

  const rowsOrdenadas = useMemo(() => {
    if (!ordenacao) return rows;
    const valor = (row: RateioRowState): string | number => {
      const uc = ucsById.get(row.ucId);
      switch (ordenacao.campo) {
        case 'uc':
          return numeroUc(uc);
        case 'titular':
          return nomeTitular(uc);
        case 'consumo':
          return uc?.consumo_medio_kwh ?? -Infinity;
        case 'saldo':
          return (historicoPorUc.get(row.ucId) || [])[0]?.saldo_acumulado_kwh ?? -Infinity;
        case 'percent':
          return row.percent;
        case 'status':
          return uc?.situacao || '';
      }
    };
    const copia = [...rows];
    copia.sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return ordenacao.direcao === 'asc' ? cmp : -cmp;
    });
    return copia;
  }, [rows, ordenacao, ucsById, historicoPorUc]);

  const renderPopover = () => {
    if (!deltaAberto) return null;
    const uc = ucsById.get(deltaAberto.ucId);
    const historico = historicoPorUc.get(deltaAberto.ucId) || [];
    const delta = calcularDelta(historico, consumoOperacionalDaUc(uc).valorKwh ?? 0);
    const row = rowsByUcId.get(deltaAberto.ucId);
    const podeAplicar = !readOnly && !(row?.locked && modalidade === 'percentual_fixo');
    return (
      <DeltaPopover
        anchor={deltaAberto.anchor}
        onClose={() => setDeltaAberto(null)}
        delta={delta}
        consumoMedioKwh={uc?.consumo_medio_kwh || 0}
        podeAplicar={podeAplicar}
        onAplicarSugestao={() => {
          if (delta) onAplicarSugestaoDelta(deltaAberto.ucId, delta.consumoUltimoMesKwh);
        }}
      />
    );
  };

  if (modalidade === 'percentual_fixo') {
    return (
      <div>
        {/* Mobile — Seção 1: "mobile = visualização simplificada, somente leitura/cards". */}
        <div className="md:hidden space-y-2">
          {rowsOrdenadas.map(row => {
            const uc = ucsById.get(row.ucId);
            const historico = historicoPorUc.get(row.ucId) || [];
            const delta = calcularDelta(historico, consumoOperacionalDaUc(uc).valorKwh ?? 0);
            return (
              <div
                key={row.ucId}
                className={`rounded-xl border border-gray-100 dark:border-gray-800 p-3 ${linhaDestaque(row.ucId)}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {numeroUc(uc)}
                      <GdBadge modalidade={uc?.modalidade_gd} />
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{nomeTitular(uc)}</p>
                  </div>
                  <StatusTag situacao={uc?.situacao} />
                </div>
                <div className="flex items-center justify-between mt-2 text-sm text-gray-600 dark:text-gray-300">
                  <span>
                    {row.percent.toFixed(2)}% · {formatKwh(row.energiaAlvoKwh)} kWh
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <ChipSaldoBaixo cobertura={coberturaSaldoPorUc?.get(row.ucId)} />
                    {delta?.alerta ? <AlertTriangle size={14} className="text-amber-500 shrink-0" /> : null}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <SortableHeaderRow ordenacao={ordenacao} onSort={onSort} />
          <tbody>
            {rowsOrdenadas.map(row => {
              const uc = ucsById.get(row.ucId);
              const historico = historicoPorUc.get(row.ucId) || [];
              const delta = calcularDelta(historico, consumoOperacionalDaUc(uc).valorKwh ?? 0);
              return (
                <tr key={row.ucId} className={`border-b border-gray-100 dark:border-gray-800 ${linhaDestaque(row.ucId)}`}>
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                    {numeroUc(uc)}
                    <GdBadge modalidade={uc?.modalidade_gd} />
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{nomeTitular(uc)}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300"><ConsumoComProjecao uc={uc} projecao={projecaoPorUc?.get(uc?.id ?? -1)} /></td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                    <SaldoComCobertura
                      saldoMesKwh={historico[0]?.saldo_acumulado_kwh}
                      cobertura={coberturaSaldoPorUc?.get(row.ucId)}
                    />
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{MARGEM_ERRO_PADRAO_PERCENT.toFixed(2)}%</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={row.percent}
                        disabled={readOnly || row.locked}
                        onChange={e => onEditarPercentual(row.ucId, parseFloat(e.target.value) || 0)}
                        className="w-24 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                      <DeltaIcon alerta={!!delta?.alerta} onOpen={anchor => setDeltaAberto({ ucId: row.ucId, anchor })} />
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                    {percentualAnteriorPorUc?.get(row.ucId) != null
                      ? `${percentualAnteriorPorUc.get(row.ucId)!.toFixed(2)}%`
                      : '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{formatKwh(row.energiaAlvoKwh)}</td>
                  <td className="py-2 pr-3">
                    <StatusTag situacao={uc?.situacao} />
                  </td>
                  <td className="py-2 pr-3">
                    <PrevisaoRecebimento vinculadoEm={uc?.rateio_vinculado_em} diaLeituraPadrao={diaLeituraPadrao} />
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        type="button"
                        onClick={() => onToggleLock(row.ucId)}
                        disabled={readOnly}
                        title={row.locked ? 'Destravar percentual (auto-balanceamento)' : 'Travar percentual (auto-balanceamento)'}
                        className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          row.locked
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {row.locked ? <Lock size={16} /> : <Unlock size={16} />}
                      </button>
                      {uc && <UcAcoesMenu uc={uc} onChanged={onUcAlterada} disabled={readOnly} />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {renderPopover()}
      </div>
    );
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    onDragEnd(result.source.index, result.destination.index);
  };

  return (
    <div>
      {/* Mobile — Seção 1: "mobile = visualização simplificada, somente leitura/cards".
          A ordem de prioridade em si só é editável via drag&drop (desktop). */}
      <div className="md:hidden space-y-2">
        {ordemPrioridade.map((p, index) => {
          const uc = ucsById.get(p.ucId);
          const row = rowsByUcId.get(p.ucId);
          const historico = historicoPorUc.get(p.ucId) || [];
          const delta = calcularDelta(historico, consumoOperacionalDaUc(uc).valorKwh ?? 0);
          return (
            <div
              key={p.ucId}
              className={`rounded-xl border border-gray-100 dark:border-gray-800 p-3 ${linhaDestaque(p.ucId)}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {index + 1}º · {numeroUc(uc)}
                    <GdBadge modalidade={uc?.modalidade_gd} />
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{nomeTitular(uc)}</p>
                </div>
                <StatusTag situacao={uc?.situacao} />
              </div>
              <div className="flex items-center justify-between mt-2 text-sm text-gray-600 dark:text-gray-300">
                <span>
                  {row ? row.percent.toFixed(2) : '0,00'}% · {row ? formatKwh(row.energiaAlvoKwh) : '0,00'} kWh
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <ChipSaldoBaixo cobertura={coberturaSaldoPorUc?.get(p.ucId)} />
                  {delta?.alerta ? <AlertTriangle size={14} className="text-amber-500 shrink-0" /> : null}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm">
        <HeaderRow modalidade={modalidade} />
        <DragDropContext onDragEnd={handleDragEnd}>
          <StrictModeDroppable droppableId="rateio-ordem-prioridade">
            {provided => (
              <tbody ref={provided.innerRef} {...provided.droppableProps}>
                {ordemPrioridade.map((p, index) => {
                  const uc = ucsById.get(p.ucId);
                  const row = rowsByUcId.get(p.ucId);
                  const historico = historicoPorUc.get(p.ucId) || [];
                  const delta = calcularDelta(historico, consumoOperacionalDaUc(uc).valorKwh ?? 0);
                  return (
                    <Draggable key={p.ucId} draggableId={String(p.ucId)} index={index} isDragDisabled={readOnly}>
                      {draggableProvided => (
                        <tr
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={`border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800 ${linhaDestaque(p.ucId)}`}
                        >
                          <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                            {numeroUc(uc)}
                            <GdBadge modalidade={uc?.modalidade_gd} />
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{nomeTitular(uc)}</td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300"><ConsumoComProjecao uc={uc} projecao={projecaoPorUc?.get(uc?.id ?? -1)} /></td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                            <SaldoComCobertura
                              saldoMesKwh={historico[0]?.saldo_acumulado_kwh}
                              cobertura={coberturaSaldoPorUc?.get(p.ucId)}
                            />
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{MARGEM_ERRO_PADRAO_PERCENT.toFixed(2)}%</td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                            <div className="flex items-center gap-1.5">
                              {row ? row.percent.toFixed(2) : '0,00'}
                              <DeltaIcon alerta={!!delta?.alerta} onOpen={anchor => setDeltaAberto({ ucId: p.ucId, anchor })} />
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                            {percentualAnteriorPorUc?.get(p.ucId) != null
                              ? `${percentualAnteriorPorUc.get(p.ucId)!.toFixed(2)}%`
                              : '—'}
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{row ? formatKwh(row.energiaAlvoKwh) : '0,00'}</td>
                          <td className="py-2 pr-3">
                            <StatusTag situacao={uc?.situacao} />
                          </td>
                          <td className="py-2 pr-3">
                            <PrevisaoRecebimento vinculadoEm={uc?.rateio_vinculado_em} diaLeituraPadrao={diaLeituraPadrao} />
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <div className="flex items-center justify-end gap-0.5">
                              {!readOnly && (
                                <span
                                  {...draggableProvided.dragHandleProps}
                                  className="inline-flex p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-grab active:cursor-grabbing"
                                  title="Arraste para reordenar prioridade"
                                >
                                  <GripVertical size={16} />
                                </span>
                              )}
                              {uc && <UcAcoesMenu uc={uc} onChanged={onUcAlterada} disabled={readOnly} />}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </tbody>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      </table>
      </div>
      {renderPopover()}
    </div>
  );
};

export default RateioTabelaUCs;
